import { motion } from 'framer-motion';
import { useState } from 'react';
import Image from 'next/image';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function ImageGallery({
  images,
  parkImages = [],
  eventTitle,
  className = '',
  showTitle = true,
}) {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  // Combine event images and park images
  const allImages = [...(images || []), ...(parkImages || [])];

  if (!allImages || allImages.length === 0) {
    return null;
  }

  // Helper function to get the correct image source
  const getImageSrc = (img) => {
    if (!img) return null;
    if (typeof img === 'string') return img;
    if (img.url) {
      // If it's already a full URL, return as is
      if (img.url.startsWith('http')) {
        return img.url;
      }
      // If it's a relative URL, make it absolute
      if (img.url.startsWith('/')) {
        return `https://www.nps.gov${img.url}`;
      }
      return img.url;
    }
    return null;
  };

  const nextImage = () => {
    setSelectedImageIndex((prev) => (prev + 1) % allImages.length);
  };

  const prevImage = () => {
    setSelectedImageIndex(
      (prev) => (prev - 1 + allImages.length) % allImages.length
    );
  };

  const currentImage = allImages[selectedImageIndex];

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.1 }}
        className={`${className}`}
      >
        {showTitle && (
          <h3 className="text-lg font-semibold text-brown-200 mb-4">
            {parkImages && parkImages.length > 0
              ? 'Event & Park Photos'
              : 'Park Photos'}
          </h3>
        )}

        <div className="space-y-4">
          {/* Main featured image - now square aspect ratio */}
          <div className="relative">
            <div className="relative overflow-hidden h-[400px] md:h-[700px] w-full">
              <Image
                src={
                  getImageSrc(currentImage) || '/placeholder/placeholder.png'
                }
                alt={
                  currentImage?.altText ||
                  `Photo ${selectedImageIndex + 1} of ${eventTitle}`
                }
                fill
                className="object-contain w-auto m-auto w-auto"
                unoptimized={getImageSrc(currentImage)?.startsWith('https://')}
              />

              {/* Image counter */}
              <div className="absolute top-3 right-3 bg-black/60 text-white px-2 py-1 pt-2 rounded-full text-xxs font-bold">
                {selectedImageIndex + 1} of {allImages.length}
              </div>
            </div>

            {/* Navigation arrows */}
            {allImages.length > 1 && (
              <>
                <button
                  onClick={prevImage}
                  className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors duration-200"
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>

                <button
                  onClick={nextImage}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition-colors duration-200"
                  aria-label="Next image"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </>
            )}
          </div>

          {/* Image caption */}
          {(currentImage?.caption || currentImage?.credit) && (
            <div className="text-center">
              {currentImage.caption && (
                <p className="text-sm text-brown-200 mb-1">
                  {currentImage.caption}
                </p>
              )}
              {currentImage.credit && (
                <p className="text-xs text-brown-100">
                  Credit: {currentImage.credit}
                </p>
              )}
            </div>
          )}

          {/* Thumbnail grid - now square aspect ratio */}
          <div className="grid grid-cols-6 gap-2">
            {allImages.map((image, index) => (
              <div
                key={index}
                className={`relative aspect-square bg-beige-100 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${
                  index === selectedImageIndex
                    ? 'ring-2 ring-green-100 ring-offset-2'
                    : 'hover:opacity-80'
                }`}
                onClick={() => setSelectedImageIndex(index)}
              >
                <Image
                  src={getImageSrc(image) || '/placeholder/placeholder.png'}
                  alt={image?.altText || `Thumbnail ${index + 1}`}
                  fill
                  className="object-cover"
                  unoptimized={getImageSrc(image)?.startsWith('https://')}
                />
              </div>
            ))}
          </div>
        </div>
      </motion.div>
    </>
  );
}
